import { useMutation, useQueryClient } from '@tanstack/react-query';
import { v4 as uuid } from 'uuid';
import request from '@teetsh/app/src/utils/request';
import { serializeSequence } from './serializer';
import { Sequence } from '@teetsh/app/src/types/sequences';
import { SEQUENCES_LIST_KEY } from './useSequencesFolders';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { useTranslation } from 'react-i18next';
import { useDefaultSequenceCycle } from 'containers/Sequences/defaultCycleAtom';
import { useNavigate } from 'react-router-dom';

function createSequenceRequest({
  sequence,
  folderId,
  templateId,
}: {
  sequence?: Sequence;
  folderId?: string;
  templateId?: string;
}): Promise<Sequence> {
  let requestURL = '/sequences';
  if (templateId) {
    requestURL += `?strapi-template-id=${templateId}`;
  }
  return request(requestURL, {
    method: 'POST',
    body: sequence
      ? serializeSequence({ ...sequence, id: uuid(), folderId })
      : { folderId },
  });
}

export function useCreateSequence() {
  const { notifyError } = useNotification();
  const queryClient = useQueryClient();
  const { t } = useTranslation(['sequence']);
  const { mutateAsync } = useMutation({
    mutationFn: ({
      sequence,
      folderId,
      templateId,
    }: {
      sequence?: Sequence;
      folderId?: string;
      templateId?: string;
    }) =>
      createSequenceRequest({
        sequence,
        folderId,
        templateId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SEQUENCES_LIST_KEY],
      });
    },
    onError: () => {
      notifyError({
        title: t('sequence:messages.sequence.createError'),
      });
    },
  });
  return mutateAsync;
}

export function useCreateEmptySequence() {
  const defaultCycle = useDefaultSequenceCycle();
  const navigate = useNavigate();
  const createSequence = useCreateSequence();
  return ({ folderId }: { folderId?: string }) => {
    const newSequenceId = uuid();
    return createSequence({
      sequence: {
        id: newSequenceId,
        title: '',
        peers: [],
        pendingInvitations: [],
        permission: 'ADMIN',
        order: 1,
        customFields: [],
        bo: {
          cycle: defaultCycle,
          matiere: [],
          domaine: [],
          goals: [],
          competence: [],
        },
        niveaux: [],
        fichesDePrep: [],
      },
      folderId: folderId === 'root' ? undefined : folderId,
    }).then(({ id }) => {
      navigate(`/sequences/${id}`);
    });
  };
}
